import type { RequestOptions, Variables } from 'graphql-request'
import { gql, GraphQLClient } from 'graphql-request'

import { AccessToken } from '../models/paramsModel'
import { MyGraphQlError } from '../services/GraphqlError.ts'

/**
 * Opciones compartidas por los servicios que utilizan el cliente GraphQL.
 * Los headers se aplican solo a la petición y la señal permite cancelarla.
 * El token de autenticación lo agrega el cliente compartido.
 *
 * @example
 * ```ts
 * import { graphqlClient } from '../../../base/api/graphqlClient.ts'
 * import type { GraphqlClientOptionsProps } from '../../../base/api/graphqlClient.ts'
 *
 * // En el servicio, usando el documento GraphQL `query` y los tipos del listado:
 * const apiPedidoListado = async (
 *   params: ApiPedidoListadoParams,
 *   options: GraphqlClientOptionsProps = {},
 * ): Promise<ApiPedidoListadoResponse> => {
 *   const data = await graphqlClient.request<
 *     { restPedidoListado: ApiPedidoListadoResponse },
 *     ApiPedidoListadoParams
 *   >({
 *     document: query,
 *     variables: params,
 *     requestHeaders: options.headers,
 *     signal: options.signal,
 *   })
 *   return data.restPedidoListado
 * }
 *
 * // Dentro de queryFn de useQuery o useMrtQuery, con los parámetros preparados:
 * return apiPedidoListado(params, {
 *   headers: { 'X-Mi-Contexto': 'valor' },
 *   signal: ctx.signal,
 * })
 * ```
 */
export interface GraphqlClientOptionsProps {
  /** Headers adicionales solo para esta petición. */
  headers?: HeadersInit
  signal?: AbortSignal
}

const endpoint = import.meta.env.ISI_API_URL

if (!endpoint) {
  console.warn('⚠️ [GraphQLClient] ISI_API_URL no está definida en las variables de entorno.')
}

/**
 * Cliente GraphQL para realizar operaciones.
 * Utiliza el endpoint definido en el entorno, o un endpoint vacío si no se encuentra.
 * Agrega un token de autenticación al header si se encuentra en localStorage.
 */
const client = new GraphQLClient(endpoint || '', {
  headers: (): Record<string, string> => {
    const token = localStorage.getItem(AccessToken)
    const headers: Record<string, string> = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return headers
  },
})

/** Opciones tipadas de una operación, con respuesta y variables independientes. */
export type GraphqlRequestOptions<TData, TVariables extends Variables = Variables> = RequestOptions<
  TVariables,
  TData
>

/** API con una sola firma para facilitar el autocompletado de las opciones.
 * @example
 * ```ts
 * import { graphqlClient } from '../../../base/api/graphqlClient.ts'
 * import type { GraphqlClientOptionsProps } from '../../../base/api/graphqlClient.ts'
 *
 * // En el servicio, usando el documento GraphQL `query` y los tipos del listado:
 * const apiPedidoListado = async (
 *   params: ApiPedidoListadoParams,
 *   options: GraphqlClientOptionsProps = {},
 * ): Promise<ApiPedidoListadoResponse> => {
 *   const data = await graphqlClient.request<
 *     { restPedidoListado: ApiPedidoListadoResponse },
 *     ApiPedidoListadoParams
 *   >({
 *     document: query,
 *     variables: params,
 *     requestHeaders: options.headers,
 *     signal: options.signal,
 *   })
 *   return data.restPedidoListado
 * }
 *
 * // Dentro de queryFn de useQuery o useMrtQuery, con los parámetros preparados:
 * return apiPedidoListado(params, {
 *   headers: { 'X-Mi-Contexto': 'valor' },
 *   signal: ctx.signal,
 * })
 * ```
 * @author isi-template
 */
export const graphqlClient = {
  request<TData, TVariables extends Variables = Variables>(
    options: GraphqlRequestOptions<TData, TVariables>,
  ): Promise<TData> {
    return client.request<TData, TVariables>(options)
  },
}

/**
 * true si el error viene de cancelar la petición (AbortController / señal de React Query).
 * @param error Error capturado.
 * @param signal Señal de la petición, si se tiene.
 */
export const isAbortError = (error: unknown, signal?: AbortSignal | null): boolean =>
  Boolean(signal?.aborted) || (error instanceof Error && error.name === 'AbortError')

/**
 * Normaliza el error de una petición GraphQL para lanzarlo desde un servicio.
 * - Cancelación: devuelve el error original, para que React Query la reconozca y no la trate como fallo.
 * - Ya es MyGraphQlError: lo devuelve tal cual, sin volver a procesarlo.
 * - Cualquier otro: lo convierte en MyGraphQlError.
 * @example
 * } catch (error) {
 *   throw toGraphQlError(error, options.signal)
 * }
 * @param error Error capturado en el catch.
 * @param signal Señal de la petición (`options.signal`).
 */
export const toGraphQlError = (error: unknown, signal?: AbortSignal | null): Error => {
  if (isAbortError(error, signal))
    return error instanceof Error ? error : new DOMException('Aborted', 'AbortError')
  if (error instanceof MyGraphQlError) return error
  return new MyGraphQlError(error instanceof Error ? error : new Error(String(error)))
}

export { gql }
